/**
 * The status line a card shows for the instruction its button created: queued
 * (and why it is not moving), running (for how long), or how the last run ended,
 * with a link to the log. Shared so every button in the UI reports back the same
 * way — the operator should never have to wonder whether a click did anything.
 *
 * Two renderings of the same line, chosen by where it sits:
 *
 * - `banner` — a boxed Alert, for page-level notices (a step running at the top
 *   of the preparing page). It competes for attention on purpose.
 * - `inline` — an icon and a sentence, for the status a card reports about its
 *   own button. A card that stacks boxed alerts inside a boxed card reads as
 *   three warnings when it is saying "your click landed"; the inline row keeps
 *   the report next to the button without shouting. A live instruction shows a
 *   spinner instead of the info icon so "still running" is visible at a glance.
 */
import type { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, CircularProgress, Link, Stack, Typography } from '@mui/material';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import type { HostRole } from '@finvari/deployer';
import type { ActivityLine, InstructionRow } from '../lib/instructionActivity';

const ICON_SX = { fontSize: 18 } as const;

const SEVERITY_ICON: Record<ActivityLine['severity'], ReactElement> = {
    info: <InfoOutlined sx={ICON_SX} />,
    success: <CheckCircleOutlineRounded sx={ICON_SX} />,
    warning: <WarningAmberRounded sx={ICON_SX} />,
    error: <ErrorOutlineRounded sx={ICON_SX} />,
};

export interface ActivityAlertProps {
    host: HostRole;
    row: InstructionRow;
    line: ActivityLine;
    /** The instruction's title, when the reader cannot tell from context which button this is about. */
    label?: string;
    variant?: 'banner' | 'inline';
}

export function ActivityAlert({ host, row, line, label, variant = 'banner' }: ActivityAlertProps) {
    const logLink = (
        <Link component={RouterLink} to={`/instructions/${host}/${row.id}`}>
            View log
        </Link>
    );

    if (variant === 'banner') {
        return (
            <Alert severity={line.severity} variant="outlined" sx={{ py: 0 }}>
                {label && <strong>{label}: </strong>}
                {line.text} {logLink}
            </Alert>
        );
    }

    // A queued-and-being-picked-up or running instruction is the one case where
    // the operator is waiting on something live; every other severity is a
    // settled fact (or a queue that is *not* moving, which the warning icon fits).
    const live = line.inflight && line.severity === 'info';
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ display: 'flex', color: `${line.severity}.main`, mt: '2px', flexShrink: 0 }}>
                {live ? <CircularProgress size={16} sx={{ m: '1px' }} /> : SEVERITY_ICON[line.severity]}
            </Box>
            <Typography variant="body2" color="text.secondary">
                {label && (
                    <Typography component="span" variant="body2" color="text.primary" fontWeight={600}>
                        {label} ·{' '}
                    </Typography>
                )}
                {line.text} {logLink}
            </Typography>
        </Stack>
    );
}
